import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';

import { Button, Combobox, Input } from '@salesforce/design-system-react';

import * as Util from '../../../common/utilities/';

import './ExclusionRow.scss';

class ExclusionRow extends Component {
  constructor(props) {
    super(props);

    this.selectionFor = this.selectionFor.bind(this);
    this.updateFor = this.updateFor.bind(this);
    this.variantFor = this.variantFor.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (Util.propChanged(prevProps, this.props, 'assetKey')) {
      this.props.onRuleChange({
        assetValue: ''
      });
    }

    if (Util.propChanged(prevProps, this.props, 'consumerKey')) {
      this.props.onRuleChange({
        consumerValue: ''
      });
    }
  }

  renderDeleteButton() {
    return (
      <Button
        className="exclusion-delete-rule-button"
        iconCategory="utility"
        iconName="delete"
        iconVariant="border-filled"
        variant="icon"
        onClick={this.props.onDeleteRule}
      />
    );
  }

  currentValue(selectionType) {
    return this.props[selectionType];
  }

  selectionFor(selectionType, value) {
    return {
      id: `${selectionType}-${value}`,
      label: value,
      selectionType,
      value
    };
  }

  currentSelection(selectionType) {
    const value = this.currentValue(selectionType);
    return value ? [this.selectionFor(selectionType, value)] : [];
  }

  variantFor(selectionType) {
    switch (selectionType) {
      case 'assetKey':
        return this.props.assetAttributes.length ? 'readonly' : undefined;
      case 'consumerKey':
        return this.props.consumerAttributes.length ? 'readonly' : undefined;
      default:
        return 'readonly';
    }
  }

  optionsFor(selectionType) {
    switch (selectionType) {
      case 'assetKey':
        if (this.props.assetAttributes) {
          return this.props.assetAttributes.map(value =>
            this.selectionFor(selectionType, value.name)
          );
        }
        break;
      case 'assetValue':
        if (this.props.assetKey && this.props.assetAttributes.length) {
          const attribute = this.props.assetAttributes.find(
            attr => attr.name === this.props.assetKey
          );
          if (attribute?.values) {
            return attribute.values.map(value =>
              this.selectionFor(selectionType, value)
            );
          }
        }
        break;
      case 'consumerKey':
        if (this.props.consumerAttributes) {
          return this.props.consumerAttributes.map(value =>
            this.selectionFor(selectionType, value.name)
          );
        }
        break;
      case 'consumerValue':
        if (this.props.consumerKey && this.props.consumerAttributes.length) {
          const attribute = this.props.consumerAttributes.find(
            attr => attr.name === this.props.consumerKey
          );
          if (attribute?.values) {
            return attribute.values.map(value =>
              this.selectionFor(selectionType, value)
            );
          }
        }
        break;
      default:
        console.error('Unexpected selection type', selectionType);
    }
    return [];
  }

  updateFor(selectionType) {
    return (evt, data) => {
      if (data) {
        //get value depending on if it's a Combobox or Textbox
        const value =
          data.selection && data.selection.length > 0
            ? data.selection[0].value
            : data.value;
        const update = { [selectionType]: value };
        switch (selectionType) {
          case 'assetKey':
            update.assetValue = undefined;
            break;
          case 'consumerKey':
            update.consumerValue = undefined;
            break;
          default:
            break;
        }
        this.props.onRuleChange(update);
      }
    };
  }

  renderDropdown(selectionType) {
    const { formatMessage } = this.props.intl;
    const i18nKey = `components.ecs.rules.exclusion.${selectionType}`;
    return (
      <Combobox
        className={`exclusion-${selectionType}-combobox`}
        options={this.optionsFor(selectionType)}
        selection={this.currentSelection(selectionType)}
        labels={{
          label: formatMessage({ id: i18nKey }),
          placeholder: formatMessage({
            id: 'components.ecs.rules.exclusion.placeholder'
          })
        }}
        required
        events={{ onSelect: this.updateFor(selectionType) }}
        variant={this.variantFor(selectionType)}
      />
    );
  }

  renderTextInput(inputType, validity) {
    const { formatMessage } = this.props.intl;
    const emptyInput = formatMessage({
      id: 'components.ecs.rules.exclusion.missing'
    });
    const i18nKey = `components.ecs.rules.exclusion.${inputType}`;
    return (
      <Input
        className={`exclusion-${inputType}-textbox`}
        value={this.currentValue(inputType) || ''}
        required={true}
        label={formatMessage({ id: i18nKey })}
        errorText={validity ? emptyInput : ''}
        onChange={this.updateFor(inputType)}
      />
    );
  }

  validateTextInput(inputType) {
    return (
      this.currentValue(inputType) === undefined ||
      this.currentValue(inputType).trim() === ''
    );
  }

  renderValueInput(valueType) {
    let selectedKey;
    let attribute;
    switch (valueType) {
      case 'assetValue':
        selectedKey = this.props.assetKey;
        attribute = this.props.assetAttributes.find(
          attr => attr.name === selectedKey
        );
        break;
      case 'consumerValue':
        selectedKey = this.props.consumerKey;
        attribute = this.props.consumerAttributes.find(
          attr => attr.name === selectedKey
        );
        break;
      default:
        console.error('Unexpected value type', valueType);
    }
    if (attribute?.values?.length) {
      return this.renderDropdown(valueType);
    } else {
      return this.renderTextInput(valueType, this.validateTextInput(valueType));
    }
  }

  render() {
    return (
      <div className="exclusion-row-container">
        <div className="exclusion-row">
          <span className="exclusion-token">{this.props.ruleNumber}</span>
          {this.renderDropdown('assetKey')}
          <span className="exclusion-token">=</span>
          {this.renderValueInput('assetValue')}
          <span className="exclusion-token">AND</span>
          {this.renderDropdown('consumerKey')}
          <span className="exclusion-token">=</span>
          {this.renderValueInput('consumerValue')}
          {this.renderDeleteButton()}
        </div>
      </div>
    );
  }
}

ExclusionRow.propTypes = {
  actions: PropTypes.object.isRequired,
  ruleNumber: PropTypes.number.isRequired,
  assetKey: PropTypes.string,
  assetValue: PropTypes.string,
  assetAttributes: PropTypes.array,
  consumerKey: PropTypes.string,
  consumerValue: PropTypes.string,
  consumerAttributes: PropTypes.array,
  onDeleteRule: PropTypes.func.isRequired,
  onRuleChange: PropTypes.func.isRequired,
  intl: intlShape.isRequired
};

export default injectIntl(ExclusionRow);
